import {
  CheckCircleOutlined,
  GoogleOutlined,
  LoginOutlined,
  LogoutOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SwapOutlined
} from "@ant-design/icons";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup
} from "@firebase/auth";
import axios from "axios";
import { useApolloClient } from "@apollo/client/react";
import { Alert, Button, Divider, Input, Select, Space, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { unregisterCurrentMessagingInstallation } from "../../firebase/firebase-installation.utils";
import { auth, getCurrentUser } from "../../firebase/firebase.utils";
import { checkUserSession, signOutStart } from "../../redux/user/user.actions";
import {
  clearMfaSession,
  getMfaSession,
  MFA_CHALLENGE_REQUIRED_EVENT,
  MFA_POLICY_CHANGED_EVENT,
  MFA_SESSION_CHANGED_EVENT,
  setMfaSession
} from "../../utils/mfaSession";
import LoadingSpinner from "../loading-spinner/loading-spinner.component";
import AuthShell from "../auth-shell/auth-shell.component";
import MfaEnrollmentQr from "./mfa-enrollment-qr.component";
import { ACTIVATE_MFA_OUTAGE_SHOP, getOutageShops, isServerUnavailable } from "./mfa-outage";

const STATUS_POLL_INTERVAL_MS = 30 * 1000;

/**
 * Selects the most useful safe message from an API or client-side error.
 *
 * @param {Object} error Axios or JavaScript error.
 * @param {string} fallback Translated fallback message.
 * @returns {string} Message suitable for the MFA alert.
 */
const getErrorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

/**
 * Blocks authenticated application routes until the active shop's optional MFA policy is satisfied.
 *
 * @param {Object} props Component properties.
 * @param {React.ReactNode} props.children Protected application content.
 * @param {boolean} [props.isAuthorized=true] Whether Firebase authentication has completed successfully.
 * @returns {React.ReactElement} Protected content, an MFA flow, or a loading state.
 */
export default function MfaGate({ children, isAuthorized = true }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const client = useApolloClient();
  const [serverUnavailable, setServerUnavailable] = useState(false);
  const [status, setStatus] = useState(null);
  const [shops, setShops] = useState([]);
  const [selectedAssociationId, setSelectedAssociationId] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Refreshes the active shop policy, enrollment state, and shop-bound MFA session status.
   *
   * @returns {Promise<void>}
   */
  const refreshStatus = useCallback(async () => {
    setLoading(true);
    try {
      const firebaseUser = auth.currentUser || (await getCurrentUser());
      if (!firebaseUser) throw new Error(t("mfa.errors.status"));
      const firebaseToken = await firebaseUser.getIdToken();
      const response = await axios.get("mfa/status", {
        timeout: 10000,
        headers: { Authorization: `Bearer ${firebaseToken}` }
      });
      const nextStatus = response.data;
      setServerUnavailable(false);
      setStatus(nextStatus);
      setError(null);
      if (!nextStatus.required && nextStatus.sessionValid === false && getMfaSession()) clearMfaSession();
    } catch (requestError) {
      const responseError = requestError?.response?.data;
      if (responseError?.code === "mfa-required") {
        setServerUnavailable(false);
        setStatus((current) => ({
          ...current,
          enrolled: responseError.enrolled,
          required: true,
          sessionValid: false,
          shop: { id: responseError.shopId, name: responseError.shopName },
          verified: false
        }));
        setError(null);
      } else if (isServerUnavailable(requestError)) {
        setServerUnavailable(true);
        // Keep an already allowed non-MFA shop mounted until the fresh policy check completes.
        setStatus((current) => (current?.required === false ? current : null));
        setEnrollment(null);
        setShops([]);
        setSelectedAssociationId(null);
        setError(null);
        try {
          const availableShops = await getOutageShops(client, auth.currentUser?.uid);
          setShops(availableShops);
          const activeShops = availableShops.filter((shop) => shop.active);
          if (activeShops.length === 1 && activeShops[0].requiresMfa === false) {
            setStatus({ required: false, shop: activeShops[0] });
          } else {
            setStatus(null);
          }
          setSelectedAssociationId(
            availableShops.find((shop) => !shop.active && shop.requiresMfa === false)?.associationId || null
          );
        } catch {
          // Unknown membership or policy must never grant access during an outage.
          setStatus(null);
        }
      } else {
        setServerUnavailable(false);
        setStatus(null);
        setError(getErrorMessage(requestError, t("mfa.errors.status")));
      }
    } finally {
      setLoading(false);
    }
  }, [client, t]);

  useEffect(() => {
    if (!isAuthorized) return undefined;
    void refreshStatus();

    /** Refreshes MFA state in response to timers and application events. */
    const refresh = () => void refreshStatus();

    /** Ignores bubbled element focus events and refreshes only when the browser window regains focus. */
    const refreshOnWindowFocus = (event) => {
      if (event.target === window) refresh();
    };
    const interval = setInterval(refresh, STATUS_POLL_INTERVAL_MS);
    window.addEventListener("focus", refreshOnWindowFocus);
    window.addEventListener(MFA_POLICY_CHANGED_EVENT, refresh);
    window.addEventListener(MFA_SESSION_CHANGED_EVENT, refresh);
    window.addEventListener(MFA_CHALLENGE_REQUIRED_EVENT, refresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", refreshOnWindowFocus);
      window.removeEventListener(MFA_POLICY_CHANGED_EVENT, refresh);
      window.removeEventListener(MFA_SESSION_CHANGED_EVENT, refresh);
      window.removeEventListener(MFA_CHALLENGE_REQUIRED_EVENT, refresh);
    };
  }, [isAuthorized, refreshStatus]);

  useEffect(() => {
    if (!status?.sessionExpiresAt) return;
    const delay = Math.max(0, status.sessionExpiresAt * 1000 - Date.now() + 250);
    const timer = setTimeout(() => {
      clearMfaSession();
      void refreshStatus();
    }, delay);
    return () => clearTimeout(timer);
  }, [refreshStatus, status?.sessionExpiresAt]);

  useEffect(() => {
    if (!status?.required || status.verified) return;
    let active = true;
    axios
      .get("mfa/shops")
      .then(({ data }) => {
        if (!active) return;
        setShops(data.shops || []);
        setSelectedAssociationId((current) => current || data.shops?.find((shop) => !shop.active)?.associationId);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [status?.required, status?.verified]);

  useEffect(() => {
    if (!status?.required || status.enrolled || enrollment || !status.recentLogin) return;
    let active = true;
    setLoading(true);
    axios
      .post("mfa/enrollment/start")
      .then((response) => {
        if (active) setEnrollment(response.data);
      })
      .catch((requestError) => {
        if (!active) return;
        if (requestError?.response?.data?.code === "mfa-recent-login-required") {
          setStatus((current) => ({ ...current, recentLogin: false }));
        } else {
          setError(getErrorMessage(requestError, t("mfa.errors.enrollment_start")));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [enrollment, status?.enrolled, status?.recentLogin, status?.required, t]);

  /**
   * Reauthenticates the current Firebase user before starting a sensitive enrollment operation.
   *
   * @param {"password"|"google.com"} providerId Firebase provider used for reauthentication.
   * @returns {Promise<void>}
   */
  const reauthenticate = async (providerId) => {
    setSubmitting(true);
    setError(null);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error(t("mfa.errors.reauthentication"));
      if (providerId === "password") {
        await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
      } else {
        await reauthenticateWithPopup(user, new GoogleAuthProvider());
      }
      await user.getIdToken(true);
      setPassword("");
      await refreshStatus();
    } catch (requestError) {
      setError(getErrorMessage(requestError, t("mfa.errors.reauthentication")));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Submits the current TOTP code to complete enrollment or verify an existing authenticator.
   *
   * @returns {Promise<void>}
   */
  const submitCode = async () => {
    if (code.length !== 6) return;
    setSubmitting(true);
    setError(null);
    try {
      const endpoint = status.enrolled ? "mfa/verify" : "mfa/enrollment/complete";
      const response = await axios.post(endpoint, { code });
      setCode("");
      setMfaSession(response.data.sessionToken);
      dispatch(checkUserSession());
    } catch (requestError) {
      if (["mfa-enrollment-expired", "mfa-recent-login-required"].includes(requestError?.response?.data?.code)) {
        setStatus((current) => ({ ...current, recentLogin: false }));
        setEnrollment(null);
      }
      setError(getErrorMessage(requestError, t("mfa.errors.code")));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Activates another associated shop from the pre-MFA gate and reloads the application context.
   *
   * @returns {Promise<void>}
   */
  const switchShop = async () => {
    if (!selectedAssociationId) return;
    setSubmitting(true);
    setError(null);
    if (!serverUnavailable) {
      try {
        await unregisterCurrentMessagingInstallation();
      } catch {
        // Shop switching should continue if messaging cleanup is unavailable.
      }
    }
    try {
      if (serverUnavailable) {
        const availableShops = await getOutageShops(client, auth.currentUser?.uid);
        if (
          !availableShops.some((shop) => shop.associationId === selectedAssociationId && shop.requiresMfa === false)
        ) {
          throw new Error(t("mfa.errors.shop_switch"));
        }
        const { data } = await client.mutate({
          mutation: ACTIVATE_MFA_OUTAGE_SHOP,
          variables: { uid: auth.currentUser.uid, associationId: selectedAssociationId }
        });
        if (data?.active?.affected_rows !== 1) throw new Error(t("mfa.errors.shop_switch"));
      } else {
        await axios.post("mfa/shops/activate", { associationId: selectedAssociationId });
      }
      clearMfaSession({ notify: false });
      window.location.reload();
    } catch (requestError) {
      setError(getErrorMessage(requestError, t("mfa.errors.shop_switch")));
      setSubmitting(false);
    }
  };

  if (!isAuthorized) return children;
  if (loading && (!status || (status.required && !status.enrolled && !enrollment && status.recentLogin))) {
    return <LoadingSpinner message={t("mfa.labels.checking")} />;
  }
  if (status && (!status.required || status.verified)) return children;

  const providerIds = new Set(auth.currentUser?.providerData?.map((provider) => provider.providerId));
  const otherShops = shops.filter((shop) => !shop.active && (!serverUnavailable || shop.requiresMfa === false));

  return (
    <AuthShell
      icon={<SafetyCertificateOutlined />}
      subtitle={
        serverUnavailable
          ? t("mfa.labels.server_unavailable_description")
          : status
            ? t("mfa.labels.shop_requires", { shop: status.shop?.name })
            : null
      }
      title={
        serverUnavailable
          ? t("mfa.labels.server_unavailable")
          : !status
            ? t("mfa.errors.status")
            : status.enrolled
              ? t("mfa.labels.verify_title")
              : t("mfa.labels.enroll_title")
      }
      wide
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        {error ? <Alert showIcon type="error" title={error} /> : null}
        {(error && !status) || serverUnavailable ? (
          <Button block icon={<ReloadOutlined />} loading={loading} onClick={refreshStatus} type="primary">
            {t("general.actions.refresh")}
          </Button>
        ) : null}

        {!status?.enrolled && status?.recentLogin === false ? (
          <Space orientation="vertical" style={{ width: "100%" }}>
            <Alert showIcon type="info" title={t("mfa.labels.reauthenticate_description")} />
            {providerIds.has("password") ? (
              <>
                <Input.Password
                  autoComplete="current-password"
                  onChange={(event) => setPassword(event.target.value)}
                  onPressEnter={() => password && void reauthenticate("password")}
                  placeholder={t("general.labels.password")}
                  value={password}
                />
                <Button
                  block
                  disabled={!password}
                  icon={<LoginOutlined />}
                  loading={submitting}
                  onClick={() => reauthenticate("password")}
                  type="primary"
                >
                  {t("mfa.actions.confirm_identity")}
                </Button>
              </>
            ) : null}
            {providerIds.has("google.com") ? (
              <Button block icon={<GoogleOutlined />} loading={submitting} onClick={() => reauthenticate("google.com")}>
                {t("mfa.actions.confirm_with_google")}
              </Button>
            ) : null}
          </Space>
        ) : null}

        {!status?.enrolled && enrollment ? (
          <>
            <Typography.Paragraph>{t("mfa.labels.scan_instructions")}</Typography.Paragraph>
            <MfaEnrollmentQr value={enrollment.otpauthUri} label={t("mfa.labels.scan_instructions")} />
            <Typography.Paragraph className="auth-shell__manual-key">
              {t("mfa.labels.manual_key")}{" "}
              <Typography.Text code copyable>
                {enrollment.manualKey}
              </Typography.Text>
            </Typography.Paragraph>
          </>
        ) : null}

        {status?.enrolled || enrollment ? (
          <Space className="auth-shell__otp" orientation="vertical" style={{ width: "100%" }}>
            <Typography.Text className="auth-shell__field-label" strong>
              {t("mfa.labels.enter_code")}
            </Typography.Text>
            <Input.OTP
              autoComplete="one-time-code"
              formatter={(value) => value.replace(/\D/g, "")}
              length={6}
              onChange={setCode}
              separator={(index) => (index === 2 ? <span className="auth-shell__otp-separator">-</span> : null)}
              size="large"
              status={error ? "error" : undefined}
              value={code}
            />
            <Button
              block
              disabled={code.length !== 6}
              icon={<CheckCircleOutlined />}
              loading={submitting}
              onClick={submitCode}
              type="primary"
            >
              {status.enrolled ? t("mfa.actions.verify") : t("mfa.actions.finish_setup")}
            </Button>
          </Space>
        ) : null}

        {otherShops.length ? (
          <div className="auth-shell__shop-switch">
            <Divider plain>{t("mfa.labels.select_shop")}</Divider>
            <Typography.Text>
              {t(serverUnavailable ? "mfa.labels.outage_switch_description" : "mfa.labels.switch_shop_description")}
            </Typography.Text>
            <Select
              onChange={setSelectedAssociationId}
              options={otherShops.map((shop) => ({
                label: `${shop.name}${shop.requiresMfa ? ` (${t("mfa.labels.mfa_required")})` : ""}`,
                value: shop.associationId
              }))}
              placeholder={t("mfa.labels.select_shop")}
              style={{ width: "100%" }}
              value={selectedAssociationId}
            />
            <Button
              block
              disabled={!selectedAssociationId}
              icon={<SwapOutlined />}
              loading={submitting}
              onClick={switchShop}
            >
              {t("mfa.actions.switch_shop")}
            </Button>
          </div>
        ) : null}

        <Button block danger icon={<LogoutOutlined />} onClick={() => dispatch(signOutStart())} size="large">
          {t("user.actions.signout")}
        </Button>
      </Space>
    </AuthShell>
  );
}
